import { parseMoney } from './display-labels';

/**
 * The Review step's data, read off the save response — never computed here.
 *
 * ─── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 *
 * The field ticket editor has always shown a subtotal it worked out on the
 * device (`lineItems.reduce(...)`), and no tax and no total at all. The office
 * summary (pages/tickets/TicketDetail.tsx) shows subtotal, tax and total read
 * straight off the ticket row. Two answers to one question, and the field one
 * silently omits the half a hand would be asked about.
 *
 * The API already owns all of it. `recalcTicketTotals` (api: lib/ticket-writes.ts)
 * re-sums the line items, resolves the tax regime for the ticket's jurisdiction
 * and work_date, and writes `subtotal` / `tax_rate_1_*` / `tax_rate_2_*` /
 * `total_amount` back to the row — and `PUT /employee/my-tickets/:id` re-SELECTs
 * inside the same transaction, so its response carries the recalculated figures.
 *
 * So Review is save-then-review: the button saves through the endpoint Save
 * Draft already uses, and these helpers read the answer back. Nothing on this
 * screen adds a rate to a quantity.
 *
 * ─── AND WHY THE `fromServer` FLAG IS THE WHOLE POINT ──────────────────────
 *
 * Offline, `useOfflineMutation` queues the write and returns a locally merged
 * record built from the CACHED ticket — `{ ...existing, ...vars.data }`. That
 * object carries `subtotal` and `total_amount` columns, because the cached row
 * has them from the last time the server answered. They are the totals for the
 * PREVIOUS line items. Rendering them next to the rows the hand just edited is
 * the silent-blank-field failure with the sign flipped: not a gap where a
 * number should be, but a confident number for a ticket that no longer exists.
 *
 * So absence is decided by WHERE the response came from, not by whether it
 * happens to carry the keys.
 */
export interface FieldTicketReviewTotals {
  /** True only when a real HTTP response produced these figures. */
  fromServer: boolean;
  subtotal: number | null;
  tax1Label: string | null;
  tax1Amount: number | null;
  tax2Label: string | null;
  tax2Amount: number | null;
  total: number | null;
}

export interface ReviewLineItem {
  type: string;
  description: string;
  quantity: number | string | null;
  unit: string;
  rate: number | string | null;
  total: number | string | null;
}

/**
 * Did this mutation result come off the wire, or out of the offline queue?
 *
 * `useOfflineMutation` returns the axios response verbatim on the online path
 * and a hand-built `{ data: { data: record } }` on the offline one. The two are
 * structurally identical at `.data.data`, which is exactly why this cannot be
 * decided by looking for the money keys. An axios response has a numeric
 * `status`; the offline stub has no such field, and neither does the record.
 *
 * This also catches the case that a plain `isOnline` check misses: the hook
 * treats a network error as "offline" and falls through to the queue while the
 * sync store still says online. That path returns the stub too, so it lands on
 * the right side of this test.
 */
export function isServerResponse(res: unknown): boolean {
  return typeof (res as { status?: unknown } | null | undefined)?.status === 'number';
}

/**
 * The ticket row inside a save response, envelope or not.
 *
 * `'data' in body ? body.data : body`, not `body?.data || body` — the `||`
 * form reads the ENVELOPE as the record whenever the server answers
 * `{ success: true, data: null }`, and every field then reads undefined off an
 * object that is merrily truthy.
 */
function readRow(res: any): any {
  const body = res?.data;
  if (body && typeof body === 'object' && 'data' in body) return body.data ?? {};
  return body ?? {};
}

/**
 * The committed ticket row, or null when the save never reached the server.
 *
 * The caller merges this over its open ticket so the Review step's header,
 * status and ticket number are the committed ones. Null offline, deliberately:
 * the offline stub is `{ ...cachedRow, ...requestBody }`, so merging it would
 * stamp the request's camelCase keys (`workDate`, `lineItems`) onto a
 * snake_case row and leave the screen reading a shape the server never sends.
 */
/**
 * The line items the server actually committed, off a save response.
 *
 * Returns null — never `[]` — when the response carries no rows at all, which
 * is what the OFFLINE path produces: `useOfflineMutation` resolves a local stub
 * with no server rows in it. The two are opposite instructions to the caller.
 * `[]` means "the ticket has no lines now", and acting on that offline would
 * wipe the hand's rows off his own screen and, worse, reset the baseline to
 * empty so his queued mutation's deletions stopped meaning anything.
 *
 * Same absent/empty distinction the equipment prefill draws with
 * `SavedUsageState`, and the API draws with `knownLineItemIds`. It keeps being
 * the same distinction because it keeps being the same mistake.
 */
export function readSavedLineItems(res: unknown): any[] | null {
  const row = readSavedTicketRow(res);
  const rows = (row as any)?.lineItems ?? (row as any)?.line_items;
  return Array.isArray(rows) ? rows : null;
}

export function readSavedTicketRow(res: unknown): any | null {
  if (!isServerResponse(res)) return null;
  const row = readRow(res);
  return row && typeof row === 'object' ? row : null;
}

/**
 * Money for the Review step, or nothing at all.
 *
 * Every figure stays null when the save did not reach the server, so
 * fmtCurrency renders '—' and the caption explains why. A ticket whose totals
 * have not been computed yet is not a ticket worth $0.00.
 */
export function readReviewTotals(res: unknown): FieldTicketReviewTotals {
  if (!isServerResponse(res)) {
    return {
      fromServer: false,
      subtotal: null,
      tax1Label: null,
      tax1Amount: null,
      tax2Label: null,
      tax2Amount: null,
      total: null,
    };
  }
  const row = readRow(res);
  const subtotal = parseMoney(row.subtotal);
  const tax1Amount = parseMoney(row.tax_rate_1_amount ?? row.taxRate1Amount);
  const tax2Amount = parseMoney(row.tax_rate_2_amount ?? row.taxRate2Amount);
  // Same read as mapTicket() in pages/tickets/TicketDetail.tsx, including the
  // fallback: total_amount is the column the recalc writes, and the office
  // summary falls back to the sum of the parts only when at least one part
  // arrived. Both screens have to answer this question identically or the
  // whole point of showing a hand the office's numbers is lost.
  const totalAmount = parseMoney(
    row.total_amount ?? row.totalAmount ?? row.total_price ?? row.totalPrice,
  );
  const derivedTotal =
    subtotal === null && tax1Amount === null && tax2Amount === null
      ? null
      : (subtotal ?? 0) + (tax1Amount ?? 0) + (tax2Amount ?? 0);
  return {
    fromServer: true,
    subtotal,
    tax1Label: row.tax_rate_1_label || row.taxRate1Label || null,
    tax1Amount,
    tax2Label: row.tax_rate_2_label || row.taxRate2Label || null,
    tax2Amount,
    total: totalAmount ?? derivedTotal,
  };
}

/**
 * The rows to show on the Review step.
 *
 * Accepts the server's saved rows (snake_case, straight out of
 * `ticket_line_items`) and the editor's own state rows (camelCase) — the same
 * both-spellings tolerance every read on this app carries. Offline there is no
 * server answer, so the editor's rows are what the hand reviews; the line
 * amounts on them are the ones already on screen in the editor, which is why
 * they may be shown while the tax and the total may not: a row's amount is the
 * hand's own arithmetic on their own rate, and the tax regime is not.
 */
export function toReviewLineItems(rows: readonly any[] | null | undefined): ReviewLineItem[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((li) => ({
    type: li.line_item_type ?? li.lineItemType ?? '',
    description: li.description ?? '',
    unit: li.unit ?? '',
    quantity: li.quantity ?? null,
    rate: li.rate ?? null,
    total: li.total ?? null,
  }));
}

/**
 * The line items a save response committed, when it reached the server.
 *
 * `PUT /employee/my-tickets/:id` echoes the rows it wrote as `lineItems`, read
 * back inside the same transaction — so this is what the office will see, not
 * what the device sent. Falls back to the caller's local rows when the save
 * was queued, or when a response arrived without them.
 */
export function reviewLineItemsFrom(res: unknown, localRows: readonly any[]): ReviewLineItem[] {
  if (isServerResponse(res)) {
    const row = readRow(res);
    const saved = row.lineItems ?? row.line_items;
    if (Array.isArray(saved) && saved.length > 0) return toReviewLineItems(saved);
  }
  return toReviewLineItems(localRows);
}
